import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Leggere il grafico Bilancio Energetico',
    summary: 'Come interpretare il grafico ora per ora che mostra ogni fonte e utilizzo dell\'energia dopo un\'ottimizzazione.',
    body: (
      <>
        <p>
          Il grafico <strong>Bilancio Energetico</strong> appare sotto il Riepilogo KPI nella
          pagina di Ottimizzazione. Si tratta di un grafico ad area oraria con una serie per ogni
          tipo di fonte o utilizzo, con possibilità di passare dalla vista comunitaria a quella
          per edificio.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Bilancio Energetico nell'arco della giornata ottimizzata."
        />

        <h5 className="mt-3">Le serie visualizzate</h5>
        <p>Il grafico mostra fino alle seguenti serie — vengono mostrate solo quelle rilevanti per i tuoi asset:</p>
        <ul>
          <li><strong>Carico base</strong> — il consumo fisso che deve essere soddisfatto ogni ora.</li>
          <li><strong>Carico flessibile</strong> — il consumo che l'ottimizzatore può spostare nel tempo.</li>
          <li><strong>Generazione FV</strong> (comunità) o <strong>FV su tetto</strong> (per edificio).</li>
          <li><strong>Allocazione generazione comunitaria</strong> — quota della generazione comunitaria indirizzata a questo edificio (solo nella vista per edificio).</li>
          <li><strong>Generazione eolica</strong> — produzione di eventuali turbine eoliche comunitarie.</li>
          <li><strong>Importazione dalla rete</strong> — energia prelevata dalla rete in quell'ora.</li>
          <li><strong>Surplus</strong> — generazione che non è stata né consumata né stoccata in quell'ora (esportata o ridotta).</li>
          <li><strong>Carica batteria</strong> e <strong>Scarica batteria</strong> — il contributo della batteria per ogni ora.</li>
        </ul>

        <h5 className="mt-3">Pattern che indicano qualcosa</h5>
        <ul>
          <li><strong>FV elevato a metà giornata + blocco di Surplus</strong> — il solare ha prodotto più di quanto la comunità potesse utilizzare; senza una batteria più grande, il resto viene esportato o ridotto.</li>
          <li><strong>FV a mezzogiorno + Carica batteria alla stessa ora</strong> — l'ottimizzatore sta risparmiando il surplus per il picco serale.</li>
          <li><strong>Scarica batteria la sera + Importazione dalla rete contenuta</strong> — la batteria sta facendo il suo lavoro; durante i prezzi di punta è necessaria poca costosa energia dalla rete.</li>
          <li><strong>Importazione dalla rete di notte + Carica batteria alla stessa ora</strong> — ha senso solo se le tariffe notturne sono molto convenienti; altrimenti verificare il file tariffario.</li>
        </ul>
        <Tip>Confronta con la <ArticleLink to="results-battery">curva dello stato di carica della batteria</ArticleLink> — raccontano la stessa storia da due angolazioni diverse.</Tip>

        <h5 className="mt-3">Flussi Energetici Annuali (Pianificazione a Lungo Termine)</h5>
        <p>
          Un grafico diverso con un nome simile appare nei risultati di <em>Pianificazione a Lungo Termine</em> — il
          grafico a barre <strong>Flussi Energetici Annuali</strong>. In quel caso, ogni barra rappresenta un anno della
          proiezione: valori positivi = energia consumata o importata, valori negativi = energia
          esportata in rete. Usalo per vedere come si evolve il mix energetico durante la vita
          dell'investimento.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Leggere i risultati della batteria',
    summary: 'Cosa indicano la curva dello stato di carica e le serie di carica/scarica della batteria.',
    body: (
      <>
        <p>
          Quando una batteria fa parte della tua comunità, l'ottimizzatore produce una
          {' '}vista <strong>Programma Batteria</strong> con la <strong>curva dello stato di carica (SOC)
          </strong> che mostra quanto è carica la batteria in ogni ora, più le corrispondenti serie di carica e
          scarica nel <ArticleLink to="results-energy-flow">grafico Bilancio Energetico</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="Stato di carica nell'arco della giornata ottimizzata."
        />

        <h5 className="mt-3">Un pattern giornaliero sano</h5>
        <ul>
          <li>Il SOC sale durante le ore di sole (carica dal FV) o durante le ore a tariffe convenienti.</li>
          <li>Il SOC scende durante il picco serale (scarica verso i carichi, riducendo le costose importazioni dalla rete).</li>
          <li>Il SOC rimane entro i limiti configurati — i valori predefiniti sono <strong>Min 10%</strong> e
            <strong> Max 90%</strong>, impostati sull'asset batteria.</li>
          <li>La curva non dovrebbe restare piatta al minimo o al massimo per molte ore — questo
            suggerisce che la batteria è troppo piccola o sottoutilizzata.</li>
        </ul>

        <h5 className="mt-3">Impostazioni della batteria da verificare (sull'asset)</h5>
        <p>
          Il comportamento della batteria nell'ottimizzazione è determinato da queste impostazioni sulla
          pagina dell'asset della comunità o dell'edificio:
        </p>
        <ul>
          <li><strong>Capacità (kWh)</strong> — quanta energia può contenere.</li>
          <li><strong>Velocità di carica/scarica (kW)</strong> — quanto velocemente può trasferire energia in entrata e uscita.</li>
          <li><strong>Efficienza di andata e ritorno (%)</strong> — energia in uscita divisa per energia in entrata. Il valore predefinito
            è 90%. Un Li-ion reale realistico è 85–95% — valori molto più bassi significa che le perdite vengono
            modellate in modo aggressivo.</li>
          <li><strong>Min / Max SOC (%)</strong> — la finestra operativa, predefinita 10%–90%.</li>
          <li><strong>SOC iniziale (%)</strong> — il livello di partenza della batteria (predefinito 50%).</li>
        </ul>

        <h5 className="mt-3">Leggere il programma insieme al SOC</h5>
        <p>
          Confronta la curva del SOC con le serie Carica batteria / Scarica batteria nel
          {' '}<ArticleLink to="results-energy-flow">grafico Bilancio Energetico</ArticleLink>: ogni ora
          di «carica» dovrebbe far salire il SOC, ogni ora di «scarica» dovrebbe farlo scendere. Un salto nel
          SOC senza una corrispondente carica nel programma significa che i dati sono stati modificati
          tra le visualizzazioni e non dovrebbe accadere in una singola esecuzione.
        </p>
        <Warn>
          Una batteria che rimane al 100% (o al suo Max SOC) per molte ore non sta facendo nulla — o il
          FV è sovradimensionato rispetto al carico, la tariffa non ha un utile spread di prezzo, oppure
          l'obiettivo o i vincoli impediscono la scarica. Prova l'obiettivo <em>Minimizza il costo</em> con
          una tariffa Day-Ahead reale per vedere se la batteria diventa più attiva.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Leggere i risultati della Pianificazione a Lungo Termine (CBA)',
    summary: 'NPV, BCR, recupero, risparmi annuali, CO₂ — cosa significa ogni valore e come confrontare gli scenari.',
    body: (
      <>
        <p>
          <strong>Pianificazione a Lungo Termine</strong> confronta uno o più scenari di investimento (nuovo FV,
          nuova batteria, nuovi caricatori EV, ecc.) rispetto alla <em>baseline</em> di non fare nulla,
          su un orizzonte temporale a tua scelta (tipicamente 5, 10 o 20 anni). Il risultato è una
          tabella comparativa di KPI finanziari e grafici affiancati.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Confronto scenari dalla Pianificazione a Lungo Termine."
        />

        <h5 className="mt-3">Cosa significa ogni colonna</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Significato in parole semplici</th><th>Positivo se…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Profitto totale (in moneta odierna) nel corso della vita del progetto, dopo aver applicato il tasso di sconto.</td><td>Positivo (e il più alto possibile).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Rapporto Benefici-Costi: benefici totali divisi per i costi totali.</td><td>Superiore a 1,0.</td></tr>
            <tr><td><strong>Recupero (anni)</strong></td><td>Anni prima che i risparmi cumulativi coprano il costo iniziale.</td><td>Inferiore alla vita utile dell'asset (ad es. il solare dura 20–25 anni).</td></tr>
            <tr><td><strong>Risparmi annuali</strong></td><td>Risparmi medi per anno rispetto alla baseline (o costo annuo della rete per la riga della baseline).</td><td>Più è alto meglio è — ma confronta con il costo iniziale.</td></tr>
            <tr><td><strong>CO₂ evitata / anno</strong></td><td>Tonnellate di CO₂ non emesse per anno rispetto alla baseline (usa il fattore CO₂ della rete della tua comunità).</td><td>Più è alto meglio è; utile per la rendicontazione sulla sostenibilità.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Una nota sul tasso di sconto</h5>
        <p>
          La pagina espone un campo <strong>Tasso di sconto</strong> (predefinito 8%) e una tabella
          di sensibilità. Il NPV viene calcolato per il tasso impostato. Prova a variarlo di ±2 punti
          percentuali per vedere quanto è sensibile la tua conclusione — consulta la tabella
          <em>Sensibilità al tasso di sconto</em> nella stessa pagina.
        </p>

        <h5 className="mt-3">Come confrontare gli scenari</h5>
        <ol>
          <li>Guarda prima il <strong>NPV</strong> — è il miglior indicatore finanziario sintetico. Lo scenario con il NPV più alto è il vincitore finanziario ed è evidenziato nella pagina.</li>
          <li>Verifica con <strong>BCR</strong> e <strong>recupero</strong> — se il NPV è alto ma il recupero è di 18 anni su un asset che dura 20, la valutazione è fragile rispetto alle ipotesi.</li>
          <li>Se due scenari pareggiano sul NPV, preferisci il BCR più alto o il costo iniziale più basso.</li>
          <li>Confronta la <strong>CO₂ evitata</strong> separatamente — lo scenario migliore dal punto di vista ambientale può differire da quello migliore finanziariamente, e la pagina lo segnala con un badge.</li>
        </ol>

        <Warn>
          Un NPV positivo dipende dalle ipotesi di input — inflazione del prezzo dell'elettricità, tasso
          di sconto, vita utile degli asset. Usa la tabella di sensibilità nella pagina per verificare
          se il NPV rimane positivo con una modesta variazione del tasso di sconto; se tende facilmente
          al negativo, la valutazione non è robusta.
        </Warn>
        <Tip>
          La pagina dei risultati della piattaforma mostra gli scenari migliori dal punto di vista
          finanziario e ambientale come callout riassuntivi. Usali come lettura rapida, ma esamina
          sempre la tabella completa prima di raccomandare un investimento.
        </Tip>
      </>
    ),
  },

  // =========================================================================
]
